import { Badge } from '@pesto/ui/components/Badge';
import { Button } from '@pesto/ui/components/Button';
import { Card } from '@pesto/ui/components/Card';
import { DataList } from '@pesto/ui/components/DataList';
import { Typography } from '@pesto/ui/components/Typography';
import { Landmark, User } from 'lucide-react';

import type { CardDetailsOwner, CardDetailsViewProps, CardOwnerDetails } from '../types/Cards.types';

const toOwnerDisplayName = (owner: CardDetailsOwner): string | null => {
  const displayName = owner.displayName ?? [owner.firstName, owner.lastName].filter(Boolean).join(' ').trim();

  return displayName === '' ? null : displayName;
};

const toDefaultOwnerDetails = (owner?: CardDetailsOwner | null): CardOwnerDetails | null => {
  if (!owner) return null;

  return {
    id: owner.id,
    name: toOwnerDisplayName(owner),
    email: owner.email,
    phone: owner.phone,
  };
};

export const CardDetailsView = ({ owner, loan, ownerDetails, onOwnerClick, onLoanClick }: CardDetailsViewProps) => {
  const displayOwnerDetails = ownerDetails ?? toDefaultOwnerDetails(owner);

  return (
    <>
      <Card className="bg-background flex flex-col gap-4 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Typography variant="headerLarge">Cardholder</Typography>
            {owner?.badges?.map((badge) => {
              const BadgeIcon = badge.icon;

              return (
                <Badge key={badge.label} variant="outline" className="text-xs">
                  {BadgeIcon && <BadgeIcon className="mr-1 h-3 w-3" />}
                  {badge.label}
                </Badge>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            {owner?.id && onOwnerClick ? (
              <Button variant="outline" size="sm" onClick={onOwnerClick}>
                <User className="h-4 w-4" />
                View User
              </Button>
            ) : null}
            {loan?.id && onLoanClick ? (
              <Button variant="outline" size="sm" onClick={onLoanClick}>
                <Landmark className="h-4 w-4" />
                <span>View Loan</span>
                {loan.partnerAccountNumber ? (
                  <span className="text-muted-foreground font-mono">{loan.partnerAccountNumber}</span>
                ) : null}
              </Button>
            ) : null}
          </div>
        </div>
        {displayOwnerDetails ? (
          <DataList root={displayOwnerDetails} />
        ) : (
          <Typography variant="body" className="text-muted-foreground">
            No cardholder linked to this card.
          </Typography>
        )}
      </Card>
    </>
  );
};
